/**
 * Deterministic-ish ETA source for the global progress bar.
 *
 * Polls GET /api/eta — a map of `METHOD /path` -> average request duration in ms
 * that the Go server measures live (internal/reqtiming). We normalize a fetch's
 * URL to the same `METHOD /path` key (query stripped), so a lookup is an exact
 * hit. When we have history for an endpoint the bar becomes a real countdown;
 * with no history yet it stays indeterminate. Best-effort: any failure just
 * leaves the previous map (or empty), and the bar falls back gracefully.
 */
import { setEstimator } from './progress';

let etaMap: Record<string, number> = {};
let installed = false;

function keyFor(method: string, url: string): string {
  let path = url;
  try {
    const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    path = new URL(url, base).pathname;
  } catch {
    path = url.split('?')[0].split('#')[0];
  }
  return `${method.toUpperCase()} ${path}`;
}

/** Predicted duration (ms) for a request, or null if we have no history. */
export function estimate(method: string, url: string): number | null {
  const v = etaMap[keyFor(method, url)];
  return typeof v === 'number' && v > 0 ? v : null;
}

async function refresh() {
  try {
    const res = await fetch('/api/eta', { credentials: 'include' });
    if (res.ok) {
      const data = (await res.json()) as unknown;
      if (data && typeof data === 'object') {
        etaMap = data as Record<string, number>;
      }
    }
  } catch {
    /* keep the previous map; the bar just stays indeterminate for now */
  }
}

/** Wire the estimator + start polling. Call once from main.tsx. */
export function installEta(intervalMs = 60000) {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  setEstimator(estimate);
  void refresh();
  window.setInterval(() => {
    void refresh();
  }, intervalMs);
}

/** Test hooks. */
export function _setEtaMapForTest(m: Record<string, number>) {
  etaMap = m;
}
export function _resetEta() {
  etaMap = {};
  installed = false;
  setEstimator(null);
}
